'use client';

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { useRipple } from './effects/ripple';
import { cn } from './lib/utils';
import { useFlatUI } from './flat-ui';
import { Colors } from './types';

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    color?: 'default' | Colors;
    size?: 'sm' | 'md' | 'lg';
  }
>(({ className, color = 'default', size = 'md', ...props }, ref) => {
  const flat = useFlatUI();
  const { rippleHandlers, rippleClassName } = useRipple({
    variant: 'ghost',
  });

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        'peer h-4 w-4 shrink-0 rounded-[2px] bg-transparent border-2 shadow transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        flat && 'shadow-none',
        {
          'border-secondary hover:bg-secondary/10': true,

          'data-[state=checked]:bg-primary data-[state=checked]:hover:bg-primary-hover data-[state=checked]:hover:border-primary-hover data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:hover:bg-primary-hover data-[state=indeterminate]:hover:border-primary-hover data-[state=indeterminate]:text-primary-foreground':
            color === 'default' || color === 'primary',

          'data-[state=checked]:bg-secondary data-[state=checked]:hover:bg-secondary-hover data-[state=checked]:hover:border-secondary-hover data-[state=checked]:border-secondary data-[state=checked]:text-secondary-foreground data-[state=indeterminate]:border-secondary data-[state=indeterminate]:bg-secondary data-[state=indeterminate]:hover:bg-secondary-hover data-[state=indeterminate]:hover:border-secondary-hover data-[state=indeterminate]:text-secondary-foreground':
            color === 'secondary',

          'data-[state=checked]:bg-success data-[state=checked]:hover:bg-success-hover data-[state=checked]:hover:border-success-hover data-[state=checked]:border-success data-[state=checked]:text-success-foreground data-[state=indeterminate]:border-success data-[state=indeterminate]:bg-success data-[state=indeterminate]:hover:bg-success-hover data-[state=indeterminate]:hover:border-success-hover data-[state=indeterminate]:text-success-foreground':
            color === 'success',

          'data-[state=checked]:bg-error data-[state=checked]:hover:bg-error-hover data-[state=checked]:hover:border-error-hover data-[state=checked]:border-error data-[state=checked]:text-error-foreground data-[state=indeterminate]:border-error data-[state=indeterminate]:bg-error data-[state=indeterminate]:hover:bg-error-hover data-[state=indeterminate]:hover:border-error-hover data-[state=indeterminate]:text-error-foreground':
            color === 'error',

          'data-[state=checked]:bg-info data-[state=checked]:hover:bg-info-hover data-[state=checked]:hover:border-info-hover data-[state=checked]:border-info data-[state=checked]:text-info-foreground data-[state=indeterminate]:border-info data-[state=indeterminate]:bg-info data-[state=indeterminate]:hover:bg-info-hover data-[state=indeterminate]:hover:border-info-hover data-[state=indeterminate]:text-info-foreground':
            color === 'info',

          'data-[state=checked]:bg-warning data-[state=checked]:hover:bg-warning-hover data-[state=checked]:hover:border-warning-hover data-[state=checked]:border-warning data-[state=checked]:text-warning-foreground data-[state=indeterminate]:border-warning data-[state=indeterminate]:bg-warning data-[state=indeterminate]:hover:bg-warning-hover data-[state=indeterminate]:hover:border-warning-hover data-[state=indeterminate]:text-warning-foreground':
            color === 'warning',
        },
        {
          'h-3 w-3': size === 'sm',
          'h-4 w-4': size === 'md',
          'h-5 w-5': size === 'lg',
        },
        rippleClassName,
        className
      )}
      {...rippleHandlers}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={cn(
          'flex items-center justify-center text-current',
          'data-[state=checked]:animate-checkbox-check',
          'data-[state=indeterminate]:animate-checkbox-check'
        )}
      >
        {props.checked === 'indeterminate' ? (
          <Minus
            className={cn('stroke-[2.5] absolute', {
              'h-3 w-3': size === 'sm',
              'h-4 w-4': size === 'md',
              'h-5 w-5': size === 'lg',
            })}
          />
        ) : (
          <Check
            className={cn('stroke-[2.5] absolute', {
              'h-3 w-3': size === 'sm',
              'h-4 w-4': size === 'md',
              'h-5 w-5': size === 'lg',
            })}
          />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
